import React from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div<{ $variant?: 'danger' | 'warning' | 'info' }>`
  background: #0d1a26;
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(244, 67, 54, 0.5)';
      case 'warning': return 'rgba(255, 193, 7, 0.5)';
      default: return 'rgba(0, 212, 255, 0.3)';
    }
  }};
  border-radius: 12px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: ${slideIn} 0.2s ease;
  overflow: hidden;
`;

const Header = styled.div<{ $variant?: 'danger' | 'warning' | 'info' }>`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(244, 67, 54, 0.1)';
      case 'warning': return 'rgba(255, 193, 7, 0.1)';
      default: return 'rgba(0, 212, 255, 0.05)';
    }
  }};
`;

const IconWrapper = styled.div<{ $variant?: 'danger' | 'warning' | 'info' }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(244, 67, 54, 0.2)';
      case 'warning': return 'rgba(255, 193, 7, 0.2)';
      default: return 'rgba(0, 212, 255, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'danger': return '#f44336';
      case 'warning': return '#ffc107';
      default: return '#00d4ff';
    }
  }};
  font-size: 18px;
`;

const Title = styled.h3`
  margin: 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
`;

const Content = styled.div`
  padding: 20px;
`;

const Message = styled.p`
  margin: 0;
  color: #b0b0b0;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid rgba(0, 212, 255, 0.1);
  justify-content: flex-end;
`;

const Button = styled.button<{ $primary?: boolean; $variant?: 'danger' | 'warning' | 'info' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$primary ? `
    background: ${props.$variant === 'danger' ? '#f44336' : props.$variant === 'warning' ? '#ffc107' : '#00d4ff'};
    border: none;
    color: ${props.$variant === 'warning' ? '#000' : '#fff'};

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  ` : `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #999;

    &:hover {
      border-color: rgba(255, 255, 255, 0.4);
      color: #fff;
    }
  `}
`;

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return '⚠';
      case 'warning':
        return '!';
      default:
        return '?';
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onCancel}>
      <ModalContainer $variant={variant} onClick={e => e.stopPropagation()}>
        <Header $variant={variant}>
          <IconWrapper $variant={variant}>{getIcon()}</IconWrapper>
          <Title>{title}</Title>
        </Header>
        <Content>
          <Message>{message}</Message>
        </Content>
        <ButtonGroup>
          <Button onClick={onCancel}>{cancelText}</Button>
          <Button $primary $variant={variant} onClick={onConfirm}>
            {confirmText}
          </Button>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

export default ConfirmModal;
